import MapView from '@arcgis/core/views/MapView';
import TileLayer from '@arcgis/core/layers/TileLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Basemap from '@arcgis/core/Basemap.js';
import LayerList from '@arcgis/core/widgets/LayerList.js';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';

import esriConfig from '@arcgis/core/config.js';
import { ArcgisMapLifecycle } from '../constants/map-life-cycle.interface';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth.service';
import { BDIKAT_TOCHNIYOT_LAYERS, HB_LAYERS, internalPortalUrl, LAYERS, onlinePortalUrl, SURVEY_LAYERS, urlHacrazotLayer, urlGushAndParcelLayer } from '../constants/constants';
import { createPopupTemplate } from '../popupTemplates/genericPopupTemplate';
import { createHacrazotPopupTemplate } from '../popupTemplates/hacrazotPopupTemplate';
import { createHeritageSitesPopupTemplate } from '../popupTemplates/heritageSitesPopupTemplate';
import { createGeoAreasPopupTemplate } from '../popupTemplates/geoAreasPopupTemplate';
import { createGushParcelPopupTemplate } from '../popupTemplates/gushParcelPopupTemplate';
import { LanguageService } from 'src/app/services/language.service';
import WebMap from '@arcgis/core/WebMap';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MainMapService implements ArcgisMapLifecycle {
	internalUser: boolean = false;
	private skipLayerPopulation: boolean = false;
	protected translate: TranslateService;
	private languageSubscription?: any;
	protected currentMapView?: MapView;
	
	constructor(translate: TranslateService, private authService: AuthService, protected languageService: LanguageService) {
		this.translate = translate;
		this.authService.internalUser$.subscribe((isInternalUser) => {
			this.internalUser = isInternalUser;
		});
		
		this.languageSubscription = this.languageService.currentLanguage$.subscribe(() => {
			this.configureMapView(this.currentMapView);
			this.updatePopupTemplates();
		});
	}

	setSkipLayerPopulation(skip: boolean): void {
		this.skipLayerPopulation = skip;
	}

	private addedLayers: any[] = [];

	onViewReady(view: MapView, basemap?: string): void {
		this.configureMapView(view);
	}

	configureMapView(view: MapView | undefined): void {
		if(!view) return;
		this.currentMapView = view;
		view.spatialReference =  { wkid: this.internalUser ? 2039 : 3857 };
		const lang = this.languageService.currentLanguage.getValue();
		if (lang === 'he') {
			const mapDiv = view.container;
			if (mapDiv && mapDiv.setAttribute) {
				mapDiv.setAttribute('dir', 'rtl');
			}
			if (view.popup) {
				(view.popup as any).direction = 'rtl';
				(view.popup as any).locale = 'he';
			}
		}else {
			const mapDiv = view.container;
			if (mapDiv && mapDiv.setAttribute) {
				mapDiv.setAttribute('dir', 'ltr');
			}
			if (view.popup) {
				(view.popup as any).direction = 'ltr';
				(view.popup as any).locale = 'en';
			}
		}
		this.initialBasemap(view);

		view.extent = this.internalUser? {
			xmin: -585659,
			ymin: 108010,
			xmax: 1099311,
			ymax: 1156916,
			spatialReference: { wkid: 2039 }
} :{
		xmin: 3600000,
		ymin: 3650000,
		xmax: 4600000,
		ymax: 4250000,
		spatialReference: { wkid: 3857 }
		};

		if (view.popup) {
			view.popup.dockEnabled = true;
			view.popup.dockOptions = {
				buttonEnabled: false,
				breakpoint: false,
				position: lang === 'he' ? "top-right" : "top-left"
			};
			view.popup.visibleElements = {
				closeButton: true
			};
		}
	}

	onBasemapGalleryReady(basemapGallery: any): void {
		this.initializeBasemapGallery(basemapGallery);
	}

	private initializeBasemapGallery(basemapGallery: any): void {
		if (!basemapGallery) {
			return;
		}

		esriConfig.portalUrl = this.internalUser
			? internalPortalUrl
			: onlinePortalUrl;

		basemapGallery.source = {
			portal: {
				url: esriConfig.portalUrl
			}
		};
	}

	onLayerListReady(layerList: LayerList): void {
		let widget = layerList;
		
		if (layerList && (layerList as any).widget) {
			widget = (layerList as any).widget;
		}
		
		if (widget) {
			this.configureLayerList(widget);
		} else {
			this.configureLayerList(layerList);
		}

		if (layerList.view && !this.skipLayerPopulation) {
			this.populateLayers(layerList.view as MapView);
		}
	}
	
	protected configureLayerList(widget: any): void {
		try {
			widget.visibleElements = {
				heading: true,
				filter: true,
				statusIndicators: true,
				errors: true
			};
			widget.filterPlaceholder = this.translate.instant('iaa.map.layer-list-filter');
			widget.headingLevel = 3;
			widget.minFilterItems = 1;
			widget.visibilityAppearance = 'checkbox';
			if (this.languageService.currentLanguage.getValue() === 'he') {
			widget.direction = 'rtl';
			widget.locale = 'he';
		}

		// Hide query results layer from LayerList
		widget.listItemCreatedFunction = (event: any) => {
			const item = event.item;
			const queryResultsLayerName = this.translate.instant('iaa.map.queryResultsLayer');
			
		// Set custom label for gush and parcel layer to include disclaimer
		if (!this.internalUser && item.layer?.id === 'gush-and-parcel-layer') {
			const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
			const disclaimer = this.translate.instant('iaa.map.gushAndParcelDisclaimer');
			item.title = `${layerTitle} - ${disclaimer}`;
		}			// Hide the query results layer from the LayerList
			if (item.layer?.title === queryResultsLayerName) {
				item.listMode = 'hide';
			}
		};
	} catch (error) {
		console.error('Error configuring LayerList:', error);
	}
}	onViewDestroyed() {
		this.addedLayers.forEach((layer) => {
			// Wrap destroy in try-catch to suppress expected AbortErrors from layer cleanup
			try {
				layer.destroy?.();
			} catch (error) {
				// Silently handle expected AbortErrors from layer cleanup
				if (error && (error as any).name !== 'AbortError') {
					console.warn('Non-critical layer cleanup error:', error);
				}
			}
		});
		this.addedLayers = [];
		this.currentMapView = undefined;
	}
	
	protected updatePopupTemplates(): void {
		if (!this.currentMapView) return;
		
		const hacrazotLayer = this.currentMapView.map?.findLayerById('hacrazot-layer') as FeatureLayer;
		if (hacrazotLayer) {
			hacrazotLayer.popupTemplate = createHacrazotPopupTemplate(this.translate);
		}
		
		const geoAreasLayer = this.currentMapView.map?.findLayerById('geo-areas-layer') as FeatureLayer;
		if (geoAreasLayer) {
			geoAreasLayer.popupTemplate = createGeoAreasPopupTemplate(this.translate);
		}
		
		const currentLang = this.languageService.currentLanguage.getValue() as 'he' | 'en';
		const gushParcelLayer = this.currentMapView.map?.findLayerById('gush-and-parcel-layer') as FeatureLayer;
		if (gushParcelLayer) {
			const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
			gushParcelLayer.popupTemplate = createGushParcelPopupTemplate(layerTitle, currentLang, this.internalUser) as any;
		}
		
		this.addedLayers.forEach(layer => {
			if (layer.type === 'feature' && layer.popupTemplate) {
				if (layer.id === 'hacrazot-layer') {
					layer.popupTemplate = createHacrazotPopupTemplate(this.translate);
				} else if (layer.id === 'heritage-sites-layer') {
					layer.popupTemplate = createHeritageSitesPopupTemplate(this.translate);
				} else if (layer.id === 'geo-areas-layer') {
					layer.popupTemplate = createGeoAreasPopupTemplate(this.translate);
				} else if (layer.id === 'gush-and-parcel-layer') {
					const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
					layer.popupTemplate = createGushParcelPopupTemplate(layerTitle, currentLang, this.internalUser) as any;
				} else {
					const title = this.translate.instant(layer.title || '');
					layer.popupTemplate = createPopupTemplate(title);
				}
			}
		});
		const bdikatTochniyotGroup = this.currentMapView.map?.findLayerById('BdikatTochniyotGroup') as GroupLayer;
		if (bdikatTochniyotGroup) {
			bdikatTochniyotGroup.title = this.translate.instant('iaa.map.bdikatTochniyotLayer');
		}

		const hbGroupLayer = this.currentMapView.map?.findLayerById('hb-group-layer') as GroupLayer;
		if (hbGroupLayer) {
			hbGroupLayer.title = this.translate.instant('iaa.map.hbGroupLayer');
		}

	const surveyGroupLayer = this.currentMapView.map?.findLayerById('survey-group-layer') as GroupLayer;
	if (surveyGroupLayer) {
		surveyGroupLayer.title = this.translate.instant('iaa.map.surveyGroupLayer');
	}

	const gushAndParcelLayer = this.currentMapView.map?.findLayerById('gush-and-parcel-layer');
	if (gushAndParcelLayer && !this.internalUser) {
		const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
		const disclaimer = this.translate.instant('iaa.map.gushAndParcelDisclaimer');
		gushAndParcelLayer.title = `${layerTitle} - ${disclaimer}`;
	}
}private async initialBasemap(view: MapView) {
  // Prevent multiple basemap creation by checking if we already have the correct basemap
  if (view.map?.basemap?.id === 'hebrew-webmap-basemap' || view.map?.basemap?.id === 'national2019') {
    return;
  }

  // Clean up any existing basemap first
  if (view.map?.basemap) {
    const oldBasemap = view.map.basemap;
    setTimeout(() => {
      try {
        oldBasemap.destroy?.();
      } catch (error) {
        if (error && (error as any).name !== 'AbortError') {
          console.warn('Non-critical basemap cleanup error:', error);
        }
      }
    }, 100);
  }

  try {
    const currentLang = this.languageService.currentLanguage.getValue();
    
    // For Hebrew non-internal users, try to load the complete webmap
    if (!this.internalUser && currentLang === 'he') {
      try {
        console.log('Loading Hebrew webmap...');
        
        const webMap = new WebMap({
          portalItem: {
            id: '2f5c0c6876fe4c498706598c5d2be528'
          }
        });
        
        await webMap.load();
        
        if (view?.map && webMap.basemap) {
          console.log('Hebrew webmap loaded successfully');
          webMap.basemap.id = 'hebrew-webmap-basemap';
          webMap.basemap.title = 'Hebrew Basemap';
          view.map.basemap = webMap.basemap;
          return;
        }
      } catch (webmapError) {
        console.warn('Failed to load Hebrew webmap, falling back to default:', webmapError);
        // Fall through to create fallback basemap
      }
    }
    
    // Create fallback basemap using getTileLayers for all other cases or if webmap fails
    const baseLayer = this.getTileLayers();
    const initialBasemap = new Basemap({
      baseLayers: [baseLayer],
      id: this.internalUser ? 'national2019' : 'fallback-basemap',
      title: this.internalUser ? 'מפת רקע מדינת ישראל' : 'Fallback Basemap',
      spatialReference: {
        wkid: this.internalUser ? 2039 : 3857,
      },
    });

    if (view?.map) {
      view.map.basemap = initialBasemap;
    }
  } catch (error) {
    if (error && (error as any).name !== 'AbortError') {
      console.warn('Non-critical basemap creation error:', error);
    }
  }
}
private populateLayers(view: MapView) {
    if (!this.internalUser) {
		esriConfig.apiKey = (window as any).gis.esriApiKey;
		let hacrazotLayer = view.map?.findLayerById('hacrazot-layer');
		if (!hacrazotLayer) {
			hacrazotLayer = new FeatureLayer({
				id: 'hacrazot-layer',
				url: urlHacrazotLayer,
				outFields: ['*'],
				popupTemplate: createHacrazotPopupTemplate(this.translate),
				title: this.translate.instant('iaa.map.hachrazaLayer'),
				opacity: 0.5,
				visible: false,
				labelsVisible: false,
			});
			view.map?.layers.add(hacrazotLayer);
		}
		let gushAndParcelLayer = view.map?.findLayerById('gush-and-parcel-layer');
		if (!gushAndParcelLayer) {
			const layerTitle = this.translate.instant('iaa.map.gushAndParcelLayer');
			const disclaimer = this.translate.instant('iaa.map.gushAndParcelDisclaimer');
			const currentLang = this.languageService.currentLanguage.getValue() as 'he' | 'en';
			
			gushAndParcelLayer = new FeatureLayer({
				id: 'gush-and-parcel-layer',
				url: urlGushAndParcelLayer,
				outFields: ['*'],
				popupTemplate: createGushParcelPopupTemplate(layerTitle, currentLang, this.internalUser) as any,
				title: `${layerTitle} - ${disclaimer}`,
				opacity: 0.4,
				visible: false,
				maxScale: 0,
				minScale: 1000000,
				labelsVisible: false,
			});
			view.map?.layers.add(gushAndParcelLayer);
		}
    }else{

    const currentLang = this.languageService.currentLanguage.getValue() as 'he' | 'en';
    const layerConfigs = [
        { id: 'hacrazot-layer', servicePath: 'HACRAZOT_SYNC/HACRAZOT/MapServer/0', translationKey: 'iaa.map.hachrazaLayer', PopupTemplate: createHacrazotPopupTemplate(this.translate)},
        { id: 'geo-areas-layer', servicePath: 'GIS_CENTRAL/GIS_NAFOT/FeatureServer/0', translationKey: 'iaa.map.geoAreasLayer', PopupTemplate: createGeoAreasPopupTemplate(this.translate) },
        { id: 'fire-zones-layer', servicePath: 'GIS_CENTRAL/IDF_FIRE_ZONES/MapServer/0', translationKey: 'iaa.map.fireZonesLayer', opacity: 0.4, PopupTemplate: createPopupTemplate(this.translate.instant('iaa.map.fireZonesLayer')) },
        { id: 'gush-and-parcel-layer', servicePath: 'GIS_CENTRAL/GUSH_AND_PARCEL/MapServer/0', translationKey: 'iaa.map.gushAndParcelLayer', opacity: 0.4, PopupTemplate: createGushParcelPopupTemplate(this.translate.instant('iaa.map.gushAndParcelLayer'), currentLang, this.internalUser) },
        { id: 'heritage-sites-layer', servicePath: 'HERITAGE_SITES/ASSET_MAIN_HERITAGE_SITES_POLYGONS_WITH_Z/MapServer/1', translationKey: 'iaa.map.heritageSitesLayer', popupTemplate: createHeritageSitesPopupTemplate(this.translate) },
		{ id: 'ba-layer', servicePath: 'BA/BA_POINTS_GLOBAL_WITH_Z/MapServer/0', translationKey: 'iaa.map.baLayer', PopupTemplate: createPopupTemplate(this.translate.instant('iaa.map.baLayer')) }
    ];
    const layers: any[] = [];

    // Feature layers
    for (const config of layerConfigs) {
        let layer = view.map?.layers.find(l => l.id === config.id);
        if (!layer) {
            layer = this.createFeatureLayer(config.id, config.servicePath, config.translationKey, config.opacity, config.PopupTemplate || config.popupTemplate);
            view.map?.layers.add(layer);
        }
        layers.push(layer);
    }

    // Group layers
    const groupLayerIds = ['BdikatTochniyotGroup', 'hb-group-layer', 'survey-group-layer'];
    const groupLayerCreators = [
        () => this.createBdikatTochniotGroup(),
        () => this.createHBGroupLayer(),
        () => this.createSurveyGroupLayer()
    ];

    groupLayerIds.forEach((id, idx) => {
        let groupLayer = view.map?.layers.find(l => l.id === id);
        if (!groupLayer) {
            groupLayer = groupLayerCreators[idx]();
            view.map?.layers.add(groupLayer);
        }
        layers.push(groupLayer);
    });

    this.addedLayers = layers;
}
}

private getTileLayers() {
  if (this.internalUser) {
    const tileLayer = new TileLayer({
      url: 'https://gis.israntique.org.il/arcgis/rest/services/MapCacheNational_2019/MapServer',
    });
    return tileLayer;
  } else {
    const currentLang = this.languageService.currentLanguage.getValue();
    if (currentLang === 'en') {
      const worldBasemapLayer = new VectorTileLayer({
        url: 'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer',
        title: 'World Basemap v2'
      });
      return worldBasemapLayer;
    } else {
      // Fallback OSM layer (will be used if webmap loading fails)
      const osmLayer = new WebTileLayer({
        urlTemplate: 'https://{subDomain}.tile.openstreetmap.org/{level}/{col}/{row}.png',
        subDomains: ['a', 'b', 'c'],
        copyright: '© OpenStreetMap contributors',
      });
      return osmLayer;
    }
  }
}

	private createFeatureLayer(id: string, servicePath: string, translationKey: string, opacity?: number, popupTemplate?: any): FeatureLayer {
		const layer = new FeatureLayer({
			id: id,
			url: `https://gis.israntique.org.il/arcgis/rest/services/${servicePath}`,
			title: `${this.translate.instant(translationKey)}`,
			visible: false,
			outFields: ['*'],
			popupTemplate: popupTemplate || null,
			labelsVisible: false,
			refreshInterval: 0,
			listMode: 'show'
		});

		if (opacity !== undefined) {
			layer.opacity = opacity;
		}

		return layer;
	}

	private createBdikatTochniotGroup(): GroupLayer {
		const bdikatTochniyotLayers = BDIKAT_TOCHNIYOT_LAYERS.map(layer => {
			const popupTemplate = createPopupTemplate(this.translate.instant(layer.translationKey));
			return this.createFeatureLayer(layer.id, layer.servicePath, layer.translationKey, undefined, popupTemplate);
		});	
		return new GroupLayer({id: 'BdikatTochniyotGroup',title: `${this.translate.instant('iaa.map.bdikatTochniyotLayer')}`,visible: false,layers: bdikatTochniyotLayers
});
	}

	private createHBGroupLayer(): GroupLayer {
		const hbLayers = HB_LAYERS.map(layer => {
			const popupTemplate = createPopupTemplate(this.translate.instant(layer.translationKey));
			return this.createFeatureLayer(layer.id, layer.servicePath, layer.translationKey, undefined, popupTemplate);
		});

		return new GroupLayer({id: 'hb-group-layer', title: `${this.translate.instant('iaa.map.hbGroupLayer')}`, visible: false, layers: hbLayers
});
	}

	private createSurveyGroupLayer(): GroupLayer {
		const surveyLayers = SURVEY_LAYERS.map(layer => {
			const popupTemplate = createPopupTemplate(this.translate.instant(layer.translationKey));
			return this.createFeatureLayer(layer.id, layer.servicePath, layer.translationKey, layer.opacity, popupTemplate);});

		return new GroupLayer({id: 'survey-group-layer', title: `${this.translate.instant('iaa.map.surveyGroupLayer')}`, visible: false,layers: surveyLayers});
	}
}
